import {useEffect, useMemo, useState} from 'react';
import {useHistory, useLocation, useParams, Redirect} from 'react-router-dom';
import gql from 'graphql-tag';
import {useQuery, useMutation} from 'services/apollo';
import {EzButton, EzHeading, EzLayout, EzPage} from '@ezcater/recipe';
import LoadingSpinner from 'components/LoadingSpinner';
import {SelfAssignDriver, DeliveryMetadata} from 'services/graphql';
import {
  DriverSelfAssignErrorCode,
  DriverSelfAssignSource,
  SelfAssignErrorTypes,
  SelfAssignMutation,
  SelfAssignMutationVariables,
} from 'services/graphql/types';
import type {
  DeliveryMetadataQuery,
  DeliveryMetadataQueryVariables,
  SelfAssignDriverMutation,
  SelfAssignDriverMutationVariables,
  SelfAssignSource,
} from 'services/graphql/types';
import {useFederationApiEppoFlag} from 'services/eppo';
import {useTranslation} from 'react-i18next';
import {useCurrentAccount} from 'components/CurrentAccount';
import SelfAssignError from 'components/SelfAssignError';
import {DELIVERIES_PATH, REQUEST_SIGN_IN_LINK_PATH} from 'paths';
import queryString from 'query-string';
import {SELF_ASSIGN_PAGE_NAME} from 'util/tracking/trackingEvents';
import {trackPageView} from 'util/tracking/trackEvent';
import Layout from '../Layout';

export const AssignMutation = gql`
  mutation SelfAssignMutation($input: DriverDeliverySelfAssignInput!) {
    selfAssign: driverDeliverySelfAssign(input: $input) {
      assignment {
        id
      }
      userErrors {
        ... on UserError {
          message
        }
        ... on DriverSelfAssignError {
          code
          message
        }
      }
    }
  }
`;

type NewSelfAssignBodyProps = {
  deliveryId: string;
};

const NewSelfAssignBody = ({deliveryId}: NewSelfAssignBodyProps) => {
  const {t} = useTranslation();
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [userErrors, setUserErrors] = useState<
    NonNullable<SelfAssignMutation['selfAssign']>['userErrors']
  >([]);
  const history = useHistory();
  const {search} = useLocation();
  const source = useMemo((): DriverSelfAssignSource | undefined => {
    const rawSource = queryString.parse(search).source as string | undefined;

    switch (rawSource) {
      case 'sms': {
        return DriverSelfAssignSource.Sms;
      }
      case 'qrcode': {
        return DriverSelfAssignSource.Qrcode;
      }
      default:
        return undefined;
    }
  }, [search]);

  const [selfAssign, selfAssignQueryResult] = useMutation<
    SelfAssignMutation,
    SelfAssignMutationVariables
  >(AssignMutation, {
    onError: e => console.error(e),
  });
  const assignmentId = selfAssignQueryResult.data?.selfAssign?.assignment?.id;

  useEffect(() => {
    const executeMutation = async () => {
      try {
        const mutationResult = await selfAssign({variables: {input: {deliveryId, source}}});
        setUserErrors(mutationResult?.data?.selfAssign?.userErrors || []);
      } finally {
        setIsInitialLoading(false);
      }
    };

    executeMutation();
  }, [deliveryId, selfAssign, source]);

  if (assignmentId) {
    return <Redirect to={`/assignment/${assignmentId}`} />;
  }

  const handleForceSelfAssign = async (): Promise<void> => {
    const mutationResult = await selfAssign({
      variables: {input: {deliveryId, replace: true, source}},
    });
    setUserErrors(mutationResult.data?.selfAssign?.userErrors || []);
  };

  const handleAssignCancelClick = (): void => {
    history.push(DELIVERIES_PATH);
  };

  const hasErrors = !!selfAssignQueryResult.error || !!userErrors?.length;
  const hasValidationUserError = userErrors?.some(e => e.__typename === 'ValidationError');
  const selfAssignUserErrors = userErrors?.filter(e => e.__typename === 'DriverSelfAssignError');
  const hasAnotherDriverAssigned = userErrors?.some(
    e => e.__typename === 'DriverSelfAssignError' && e.code === DriverSelfAssignErrorCode.Assigned
  );

  return (
    <Layout title={t('selfAssign.title')} loading={isInitialLoading}>
      <EzPage>
        {hasErrors && (
          <>
            <EzHeading size="2">{t('selfAssign.assignErrorTitle')}</EzHeading>
            {(selfAssignQueryResult.error || hasValidationUserError) && (
              <EzHeading size="4">{t('selfAssign.assignErrorDefaultMessage')}</EzHeading>
            )}
            {selfAssignUserErrors?.map((error, index) => (
              <EzHeading key={index} size="4">
                {error.message}
              </EzHeading>
            ))}
            {hasAnotherDriverAssigned && (
              <EzLayout layout="stack" alignX="center">
                <EzButton
                  disabled={selfAssignQueryResult.loading}
                  onClick={handleForceSelfAssign}
                  use="primary"
                  legacy
                >
                  {t('selfAssign.replaceConfirmButtonText')}
                </EzButton>
                <EzButton use="secondary" onClick={handleAssignCancelClick} legacy>
                  {t('selfAssign.replaceCancelButtonText')}
                </EzButton>
              </EzLayout>
            )}
          </>
        )}
      </EzPage>
    </Layout>
  );
};

type LegacySelfAssignBodyProps = {
  deliveryId: string;
};

const LegacySelfAssignBody = ({deliveryId}: LegacySelfAssignBodyProps) => {
  const {t} = useTranslation();
  const history = useHistory();
  const {search} = useLocation();
  const source = useMemo(() => queryString.parse(search).source, [search]) as
    | SelfAssignSource
    | undefined;

  const [hasErrors, setHasErrors] = useState(false);

  const {id} = useCurrentAccount();
  const isLoggedIn = !!id;

  const [selfAssign, {loading: selfAssignLoading, data: selfAssignData}] = useMutation<
    SelfAssignDriverMutation,
    SelfAssignDriverMutationVariables
  >(SelfAssignDriver, {
    onError: () => {
      setHasErrors(true);
    },
  });

  const {loading, data: assignableData} = useQuery<
    DeliveryMetadataQuery,
    DeliveryMetadataQueryVariables
  >(DeliveryMetadata, {
    variables: {id: deliveryId},
    onError: () => {
      setHasErrors(true);
    },
  });

  useEffect(() => {
    // When a driver is logged in we want to call SelfAssignDriver
    // which will decide if the driver is already assigned or render
    // the error view based on response
    if (isLoggedIn) {
      selfAssign({
        variables: {
          input: {
            deliveryId,
            source,
          },
        },
      });
    }
  }, [deliveryId, isLoggedIn, selfAssign, source]);

  useEffect(() => {
    if (selfAssignData?.selfAssignDriver?.driverAssignmentId) {
      history.push(`/assignment/${selfAssignData.selfAssignDriver.driverAssignmentId}`);
    }

    if (selfAssignData?.selfAssignDriver?.userErrors.length) {
      setHasErrors(true);
    }
  }, [history, selfAssignData]);

  const title = t('selfAssign.title');

  return loading || selfAssignLoading ? (
    <LoadingSpinner />
  ) : (
    <Layout title={title}>
      <EzPage>
        {((isLoggedIn && !assignableData?.deliveryMetadata?.assignable) || hasErrors) && (
          <SelfAssignError
            message={assignableData?.deliveryMetadata?.unassignableReason || ''}
            showOverrideOptions={
              assignableData?.deliveryMetadata?.unassignableReason === SelfAssignErrorTypes.Assigned
            }
            deliveryId={deliveryId}
            source={source}
            handleSelfAssign={selfAssign}
          />
        )}
      </EzPage>
    </Layout>
  );
};

type RouteParams = {
  deliveryId: string;
};

const SelfAssign = () => {
  const featureFlag = useFederationApiEppoFlag();
  const {deliveryId} = useParams<RouteParams>();
  const {id} = useCurrentAccount();

  useEffect(() => {
    trackPageView(SELF_ASSIGN_PAGE_NAME, {
      driver_id: id,
      delivery_id: deliveryId,
    });
  }, [deliveryId, id]);

  // If someone bypasses auth by removing the `source` URL query parameter,
  // redirect and assume source is "qrcode" so they can request a new sign in
  // link.
  if (!id) {
    const redirectTo = `${REQUEST_SIGN_IN_LINK_PATH}?deliveryId=${deliveryId}&source=qrcode`;

    return <Redirect to={redirectTo} />;
  }

  if (featureFlag) {
    return <NewSelfAssignBody deliveryId={deliveryId} />;
  }

  return <LegacySelfAssignBody deliveryId={deliveryId} />;
};

export default SelfAssign;
